import { isSettingsAdmin, teamsForCurrentUser } from './authz';
import { showAlert, hideModal, showModal } from './modal';
import { editingTeamId, persistTeams, setEditingTeamId, teams, users } from './state';

function requireAdmin(action: string): boolean {
    if (isSettingsAdmin()) {
        return true;
    }
    showAlert(`Only administrators can ${action} teams.`);
    return false;
}

export function renderTeams(): void {
    const list = document.getElementById('teamList');
    const createBtn = document.getElementById('createTeamBtn');
    if (!list) {
        return;
    }

    const admin = isSettingsAdmin();
    if (createBtn) {
        createBtn.classList.toggle('d-none', !admin);
    }

    list.innerHTML = '';
    const visibleTeams = teamsForCurrentUser();

    if (visibleTeams.length === 0) {
        const emptyMessage = admin
            ? 'No teams yet. Create one to get started.'
            : 'You are not assigned to a team yet.';
        list.innerHTML = `<div class="list-group-item text-muted">${emptyMessage}</div>`;
        return;
    }

    visibleTeams.forEach((team) => {
        const memberNames = team.members
            .map((memberId) => {
                const user = users.find((u) => u.id === memberId);
                return user ? user.name : null;
            })
            .filter((name) => name !== null)
            .join(', ');

        const item = document.createElement('div');
        item.className = 'list-group-item';
        item.innerHTML = `
            <div class="d-flex align-items-start">
                <div class="flex-grow-1">
                    <div class="fw-semibold">${team.name}</div>
                    <div class="text-muted small">${team.description}</div>
                    <div class="text-muted small"><i class="ti ti-users me-1"></i> ${memberNames || 'No members'}</div>
                </div>
                ${admin ? `
                <div class="ms-3 d-flex gap-2">
                    <button class="btn btn-sm btn-outline-primary" data-action="editTeam" data-id="${team.id}"><i class="ti ti-edit"></i></button>
                    <button class="btn btn-sm btn-outline-danger" data-action="deleteTeam" data-id="${team.id}"><i class="ti ti-trash"></i></button>
                </div>` : ''}
            </div>
        `;
        list.appendChild(item);
    });
}

export function openTeamModal(): void {
    if (!requireAdmin('create')) {
        return;
    }

    setEditingTeamId(null);
    const titleEl = document.getElementById('teamModalTitle');
    if (titleEl) {
        titleEl.textContent = 'Create New Team';
    }

    const nameInput = document.getElementById('teamName') as HTMLInputElement | null;
    const descInput = document.getElementById('teamDesc') as HTMLTextAreaElement | null;
    if (nameInput) nameInput.value = '';
    if (descInput) descInput.value = '';

    renderTeamMembersList([]);
    showModal('teamModal');
}

export function editTeam(teamId: number): void {
    if (!requireAdmin('edit')) {
        return;
    }

    const team = teams.find((t) => t.id === teamId);
    if (!team) {
        return;
    }

    setEditingTeamId(teamId);

    const titleEl = document.getElementById('teamModalTitle');
    const nameInput = document.getElementById('teamName') as HTMLInputElement | null;
    const descInput = document.getElementById('teamDesc') as HTMLTextAreaElement | null;

    if (titleEl) titleEl.textContent = 'Edit Team';
    if (nameInput) nameInput.value = team.name;
    if (descInput) descInput.value = team.description;

    renderTeamMembersList(team.members);
    showModal('teamModal');
}

export function renderTeamMembersList(selectedMembers: number[]): void {
    const container = document.getElementById('teamMembersList');
    if (!container) {
        return;
    }

    container.innerHTML = '';

    users.forEach((user) => {
        const checked = selectedMembers.includes(user.id) ? 'checked' : '';
        container.insertAdjacentHTML(
            'beforeend',
            `
            <label class="list-group-item d-flex align-items-center gap-2">
                <input class="form-check-input m-0" type="checkbox" value="${user.id}" ${checked}>
                <span>${user.name} (${user.email})</span>
            </label>
        `,
        );
    });
}

export function saveTeam(): void {
    if (!requireAdmin('update')) {
        return;
    }

    const nameInput = document.getElementById('teamName') as HTMLInputElement | null;
    const descInput = document.getElementById('teamDesc') as HTMLTextAreaElement | null;
    if (!nameInput || !descInput) {
        return;
    }

    const name = nameInput.value.trim();
    const description = descInput.value.trim();
    if (!name) {
        showAlert('Please enter a team name');
        return;
    }

    const membersList = document.getElementById('teamMembersList');
    const selectedMembers: number[] = [];
    if (membersList) {
        membersList.querySelectorAll('input[type="checkbox"]:checked').forEach((checkbox) => {
            selectedMembers.push(parseInt((checkbox as HTMLInputElement).value, 10));
        });
    }

    if (editingTeamId) {
        const team = teams.find((t) => t.id === editingTeamId);
        if (team) {
            team.name = name;
            team.description = description;
            team.members = selectedMembers;
        }
    } else {
        const newId = teams.length > 0 ? Math.max(...teams.map((t) => t.id)) + 1 : 1;
        teams.push({ id: newId, name, description, members: selectedMembers });
    }

    closeTeamModal();
    renderTeams();
    persistTeams();
}

export function deleteTeam(teamId: number): void {
    if (!requireAdmin('delete')) {
        return;
    }

    if (!confirm('Are you sure you want to delete this team?')) {
        return;
    }

    const index = teams.findIndex((t) => t.id === teamId);
    if (index !== -1) {
        teams.splice(index, 1);
        renderTeams();
        persistTeams();
    }
}

export function closeTeamModal(): void {
    hideModal('teamModal');
    setEditingTeamId(null);
}
